/**
 * MONSTER TOWN — TOWN LANDMARKS YOU CAN TAP. WORLD CREW (src/world/**).
 *
 * LAW #1 says everything you tap does something. The build system's placeables
 * get that from the type system; the town's static landmarks — the four front
 * doors, the pond, the mailbox — get it from here.
 *
 * Tap a building anywhere (roof, wall, door — a 3-year-old aims at "the shop",
 * not at a door leaf), the monster walks to its doorstep and then the door does
 * its thing: it opens into the interior if that crew has registered the scene,
 * and it bounces, sparkles and chimes if it has not. Either way something
 * visible happens, always. Nothing here can fail and nothing is timed (LAW #2).
 */

import * as THREE from 'three'
import { bus } from '../core/events'
import { wallet } from '../core/economy'
import { save } from '../core/save'
import { world } from '../core/scene'
import type { SceneId, Vec3 } from '../core/types'
import { flat, group, sphereGeo } from '../render/kit'
import type { GameContext } from '../app/context'
import { app } from '../app/context'
import { worldToCanvasPx } from '../input/pick'
import { claimTap, navGoTo } from './nav'
import type { Landmark, SceneryTap } from './terrain'

// ---------------------------------------------------------------------------
// sparkles — the world crew's own visible feedback, so a landmark never
// depends on another crew having shipped a particle system yet.
// ---------------------------------------------------------------------------

interface Spark {
  mesh: THREE.Mesh
  vx: number
  vy: number
  vz: number
  life: number
}

const sparks: Spark[] = []
let sparkRoot: THREE.Group | null = null

function initSparks(parent: THREE.Object3D): void {
  sparkRoot = group('townSparks')
  for (let i = 0; i < 10; i++) {
    const mesh = new THREE.Mesh(sphereGeo(0.17, 7), flat('flowerWhite'))
    mesh.visible = false
    mesh.castShadow = false
    sparkRoot.add(mesh)
    sparks.push({ mesh, vx: 0, vy: 0, vz: 0, life: 0 })
  }
  parent.add(sparkRoot)
}

function burst(at: Vec3, color: 'uiCoin' | 'flowerWhite' | 'uiAccent', count = 7): void {
  let used = 0
  for (const s of sparks) {
    if (used >= count) break
    if (s.life > 0) continue
    used++
    const a = (used / count) * Math.PI * 2
    s.mesh.material = flat(color)
    s.mesh.position.set(at.x, at.y + 0.6, at.z)
    s.mesh.scale.setScalar(1)
    s.mesh.visible = true
    s.vx = Math.cos(a) * 2.1
    s.vz = Math.sin(a) * 2.1
    s.vy = 3.4 + (used % 3) * 0.5
    s.life = 0.85
  }
}

// ---------------------------------------------------------------------------
// bounce
// ---------------------------------------------------------------------------

interface Bounce {
  art: THREE.Object3D
  t: number
}
const bounces: Bounce[] = []

function bounce(art: THREE.Object3D): void {
  if (bounces.some((b) => b.art === art)) return
  bounces.push({ art, t: 0 })
}

// ---------------------------------------------------------------------------
// hotspots
// ---------------------------------------------------------------------------

const landmarks: Landmark[] = []
const raycaster = new THREE.Raycaster()
let giftGiven = false

function findAt(ctx: GameContext, ndcX: number, ndcY: number, ground: Vec3 | null): Landmark | null {
  raycaster.setFromCamera(new THREE.Vector2(ndcX, ndcY), ctx.camera.camera)
  let best: Landmark | null = null
  let bestDist = Infinity
  for (const l of landmarks) {
    const hit = raycaster.intersectObject(l.art, true)
    const first = hit[0]
    if (first !== undefined && first.distance < bestDist) {
      bestDist = first.distance
      best = l
    }
  }
  if (best !== null) return best
  // Fat-finger fallback: a tap on the ground near a landmark counts as the
  // landmark. Kids aim at "the pond", not at a pixel.
  if (ground === null) return null
  for (const l of landmarks) {
    const d = Math.hypot(ground.x - l.at.x, ground.z - l.at.z)
    if (d < l.radius && d < bestDist) {
      bestDist = d
      best = l
    }
  }
  return best
}

function trigger(l: Landmark): void {
  bounce(l.art)
  burst(l.at, l.id === 'mailbox' ? 'uiCoin' : l.id === 'pond' ? 'uiAccent' : 'flowerWhite')
  bus.emit('fx:play', { effect: l.effect, at: l.at, count: 8 })
  bus.emit('audio:cue', { cue: l.cue, at: l.at })
  bus.emit('ui:toast', { icon: l.icon })

  if (l.id === 'mailbox' && !giftGiven) {
    // One little find per visit. Coins come easily and are never taken away.
    giftGiven = true
    wallet.earn('town.find', l.at)
    save.request('town.find')
  }

  if (l.panel !== undefined) {
    bus.emit('ui:panel', { panel: l.panel })
    return
  }
  const to = l.toScene
  if (to !== undefined && world.has(to as SceneId)) {
    void world.transitionTo(to as SceneId)
  }
}

// ---------------------------------------------------------------------------
// standing scenery (LAW #1 on the opening screen)
// ---------------------------------------------------------------------------

const taps: SceneryTap[] = []
/** Kept so a scenery tap can be measured in canvas pixels, never window pixels. */
let pickCtx: GameContext | null = null
let lampGlow: THREE.Mesh | null = null
let lightsOn = false
/** One little find per object per visit. Coins come easily, never a grind. */
const found = new Set<string>()

/**
 * Nearest scenery tap to a tap. Scenery is merged into a handful of meshes, so
 * this cannot be a raycast against the object — it is proximity, which is what
 * a three-year-old's finger wants anyway.
 *
 * MEASURED IN SCREEN PIXELS FIRST, ground metres second. Ground proximity alone
 * missed every TALL object: aim at a lamp post's glowing head and the tap ray
 * passes over the post and lands on the ground several metres BEHIND it, so a
 * lamp and a tree both tested as "nothing there" while a bench and a flower bed
 * — which are ankle-high, so their ray lands on themselves — worked fine. The
 * screen-space test is the same trick `pickInstance` uses for placeables, with
 * the same generous slop.
 */
const SCENERY_SLOP_PX = 64

function sceneryAt(point: { x: number; y: number }, ground: Vec3 | null): SceneryTap | null {
  let best: SceneryTap | null = null
  let bestScore = Infinity
  if (pickCtx !== null) {
    for (const t of taps) {
      const px = worldToCanvasPx(pickCtx.camera.camera, pickCtx.canvas, t.at)
      if (px.behind) continue
      const d = Math.hypot(point.x - px.x, point.y - px.y)
      if (d < SCENERY_SLOP_PX && d < bestScore) {
        bestScore = d
        best = t
      }
    }
    if (best !== null) return best
  }
  if (ground === null) return null
  for (const t of taps) {
    const d = Math.hypot(ground.x - t.at.x, ground.z - t.at.z)
    if (d < t.radius && d < bestScore) {
      bestScore = d
      best = t
    }
  }
  return best
}

function triggerScenery(t: SceneryTap): void {
  // Baked scenery is not a placeable, so using one never produced
  // `play:interacted` and nothing outside this file could tell that a kid had
  // just made the lamp posts light up. The first-run tutorial's "tap a thing"
  // beat waits on exactly that, so it is announced.
  bus.emit('world:sceneryUsed', { id: t.id, at: { x: t.at.x, y: t.at.y, z: t.at.z } })
  bus.emit('fx:play', { effect: t.effect, at: t.at, count: 10 })
  bus.emit('audio:cue', { cue: t.cue, at: t.at })
  bus.emit('ui:toast', { icon: t.icon })
  if (t.action !== undefined) bus.emit('monster:action', { action: t.action, seconds: 1.4 })

  if (t.lamp === true && lampGlow !== null) {
    // Tap ANY lamp and the whole street warms up. Every bulb shares one merged
    // mesh, so this is a single material swap — and a 3-year-old who tapped one
    // lamp post and lit up the entire town has done something worth doing.
    lightsOn = !lightsOn
    lampGlow.material = flat(lightsOn ? 'uiCoin' : 'sunWarm')
    lampGlow.scale.setScalar(lightsOn ? 1.22 : 1)
    burst(t.at, 'uiCoin', 9)
  } else {
    burst(t.at, t.effect === 'petals' ? 'flowerWhite' : 'uiAccent', 6)
  }

  if (!found.has(t.id)) {
    found.add(t.id)
    wallet.earn('town.find', t.at)
    save.request('town.find')
  }
}

/**
 * ============ HOW TALL IS THE THING A SIGN IS NAMING ============
 *
 * VR1 defect, in the most-photographed frame in the repo: *"the house-icon
 * signpost's pole enters the red roof at (265,330)"*, and it is on the roof
 * again at ten at night.
 *
 * The HUD floats a sign a FIXED number of canvas pixels above the point the
 * world gives it, and the point the world gives it is a landmark's GROUND spot
 * — the doorstep. A constant lift over a building of unknown height cannot work:
 * it is too little on a two-storey cottage (the post goes through the roof) and
 * too much on a low one. There is no number that is right for both, which is why
 * the lift has already been re-tuned twice (74, then 104) and is still wrong.
 *
 * The world crew is the only crew that knows how tall its own buildings are, so
 * it says. `top` is the world Y of the highest point of the landmark's ART, and
 * the sign hangs off THAT — its post lands on the ridge of the roof it names,
 * whatever the building and whatever the zoom.
 *
 * Measured once per landmark and cached: a bounding box over a merged building
 * is not free, and the town's statics never move.
 */
const tops = new Map<string, number>()
const topBox = new THREE.Box3()
function topOf(l: Landmark): number {
  const cached = tops.get(l.id)
  if (cached !== undefined) return cached
  topBox.setFromObject(l.art)
  // An empty or non-finite box (art that has not been built yet) must fall back
  // to the ground point rather than poison a sign with NaN.
  const y = isFinite(topBox.max.y) ? topBox.max.y : l.at.y
  tops.set(l.id, y)
  return y
}

export function installHotspots(
  ctx: GameContext,
  actors: THREE.Object3D,
  list: Landmark[],
  scenery: SceneryTap[] = [],
  glow: THREE.Mesh | null = null,
): void {
  landmarks.length = 0
  landmarks.push(...list)
  // A rebuilt town is new art at new heights; the cache must not outlive it.
  tops.clear()
  taps.length = 0
  taps.push(...scenery)
  lampGlow = glow
  pickCtx = ctx
  lightsOn = false
  found.clear()
  initSparks(actors)

  /**
   * SIGNPOSTS ASK US WHERE THE DOORS ARE (added r2 for the HUD crew).
   *
   * The town's front doors are static art with tap hotspots, not placeables and
   * not portals, so the HUD had no way to find them — which is why nothing in
   * the world ever pointed at the restaurant, the shop or the kid's own house.
   * Crews never import each other, so this answers on the bus instead. The
   * reply is synchronous, so the asker can use it inside its own rebuild and
   * neither install order matters.
   *
   * Only DESTINATIONS are announced: things that open a scene or a panel. The
   * pond is lovely and it is not somewhere to be sent.
   */
  bus.on('world:landmarksRequest', () => {
    bus.emit('world:landmarks', {
      list: landmarks
        .filter((l) => l.toScene !== undefined || l.panel !== undefined)
        .map((l) => ({
          id: l.id,
          at: { x: l.at.x, y: l.at.y, z: l.at.z },
          icon: l.icon,
          top: topOf(l),
          ...(l.toScene === undefined ? {} : { toScene: l.toScene }),
          ...(l.panel === undefined ? {} : { panel: l.panel }),
        })),
    })
  })

  /**
   * ...AND WHERE THE TAPPABLE THINGS ARE (added for the first-run tutorial).
   *
   * Same shape, same reason as the landmark reply above. A tutorial that has to
   * point at "something you can tap" cannot use the placement grid, because on a
   * brand new town the grid is empty and every tappable object out there —
   * lamp post, bench, flower bed, tree, sign — is baked static art.
   */
  bus.on('world:sceneryRequest', () => {
    bus.emit('world:scenery', {
      list: taps.map((t) => ({ id: t.id, at: { x: t.at.x, y: t.at.y, z: t.at.z } })),
    })
  })

  /**
   * A tap on a signpost is a tap on the door. Same walk, same trigger, same
   * sparkles — a sign must never be a second, subtly different way for a door
   * to behave.
   */
  bus.on('world:landmarkTap', (e) => {
    const l = landmarks.find((x) => x.id === e.id)
    if (l === undefined) return
    claimTap()
    const already = Math.hypot(app.playerPos.x - l.standAt.x, app.playerPos.z - l.standAt.z) < 2.2
    if (already) trigger(l)
    else navGoTo(l.standAt, () => trigger(l))
  })

  bus.on('input:tap', (e) => {
    if (e.mode !== 'play') return
    if (e.instanceId !== null) return // a placed thing wins; the build crew owns it
    const l = findAt(ctx, e.point.ndcX, e.point.ndcY, e.ground)
    if (l !== null) {
      // This tap is ours — stop the router's follow-up walk from overriding it.
      claimTap()
      const already = Math.hypot(app.playerPos.x - l.standAt.x, app.playerPos.z - l.standAt.z) < 2.2
      if (already) trigger(l)
      else navGoTo(l.standAt, () => trigger(l))
      return
    }
    const s = sceneryAt(e.point, e.ground)
    if (s === null) return
    claimTap()
    const near = Math.hypot(app.playerPos.x - s.standAt.x, app.playerPos.z - s.standAt.z) < 2.2
    if (near) triggerScenery(s)
    else navGoTo(s.standAt, () => triggerScenery(s))
  })
}

/** Harness/debug: every scenery point that answers a tap. */
export function sceneryTapIds(): string[] {
  return taps.map((t) => t.id)
}

/** Harness/debug: the tap points themselves, so a tool can aim at one. */
export function sceneryTaps(): readonly SceneryTap[] {
  return taps
}

/** Harness/debug: are the town's lamps lit right now? */
export function lampsAreLit(): boolean {
  return lightsOn
}

/** Called every frame by the town system. Pure visuals, no state. */
export function updateHotspots(frameMs: number): void {
  const dt = Math.min(0.05, frameMs / 1000)
  for (const s of sparks) {
    if (s.life <= 0) continue
    s.life -= dt
    s.mesh.position.x += s.vx * dt
    s.mesh.position.y += s.vy * dt
    s.mesh.position.z += s.vz * dt
    s.vy -= 9 * dt
    s.mesh.scale.setScalar(Math.max(0.01, s.life / 0.85))
    if (s.life <= 0) {
      s.mesh.visible = false
      s.life = 0
    }
  }
  for (let i = bounces.length - 1; i >= 0; i--) {
    const b = bounces[i]
    if (b === undefined) continue
    b.t += dt
    const k = Math.min(1, b.t / 0.5)
    const squash = Math.sin(k * Math.PI * 2) * 0.045 * (1 - k)
    b.art.scale.set(1 - squash, 1 + squash * 1.6, 1 - squash)
    if (k >= 1) {
      b.art.scale.set(1, 1, 1)
      bounces.splice(i, 1)
    }
  }
}

/** Harness/debug: the landmark ids a kid can tap in the town. */
export function hotspotIds(): string[] {
  return landmarks.map((l) => l.id)
}

/** Where a monster stands to use a landmark. Used by the harness spots. */
export function hotspotStand(id: string): Vec3 | null {
  return landmarks.find((l) => l.id === id)?.standAt ?? null
}
